// src/Pages/Order/Order.tsx

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrderMenus, fetchCart } from '../../Redux/Public/orderApi';
import { RootState, AppDispatch } from '../../Redux/Store';
import { OrderItem, CartItem } from '../../types/order';
import PublicHeader from '../../Components/Header/PublicHeader';
import CartList from './CartList';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';
import CategoryTabs from '../../Components/Order/CategoryTabs';
import { MenuList } from '../../Components/Order/MenuList';
import QuantitySelectionModal from '../../Components/Order/QuantitySelectionModal';
import { useCart } from '../../hooks/useCart';
import { useClerkCall } from '../../hooks/useClerkCall';
import { useCheckoutRequest } from '../../hooks/useCheckoutRequest';
import { useCheckoutRequestState } from '../../hooks/useCheckoutRequestState';
import CheckoutRequestDialog from '../../Components/Order/CheckoutRequestDialog';
import CheckoutStatusBanner from '../../Components/Order/CheckoutStatusBanner';
import PublicFooter from '../../Components/Footer/PublicFooter';
import {
  Box,
  Container,
  CircularProgress,
  Alert,
  Badge,
  Snackbar,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Notifications as NotificationsIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';

const Order: React.FC = () => {
  const { active_store_id, active_dining_table_id, clerk_id } = useParams<{
    active_store_id: string;
    active_dining_table_id: string;
    clerk_id?: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { categories, loading, error, storeName, carts, menuOptions, orderMenuTemplate } = useSelector((state: RootState) => state.order) || {};
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCart, setShowCart] = useState(false);
  // 会計受付済みで注文できないときに出す案内（ADR 0068）
  const [orderingClosedNotice, setOrderingClosedNotice] = useState(false);
  const CLOUDFRONT_URL = process.env.REACT_APP_CLOUDFRONT_URL;

  const userType = clerk_id ? 'clerk' : 'customer';
  const {
    showQuantityModal,
    setShowQuantityModal,
    selectedItem,
    quantity,
    setQuantity,
    handleAddClick,
    handleCartItemQuantity,
    handleRemoveItem,
    handleConfirmDelete,
    handleCheckoutSuccess,
    handleAddToCart,
    cartError,
    clearCartError,
  } = useCart({
    activeStoreId: active_store_id || '',
    activeDiningTableId: active_dining_table_id || '',
    userType,
  });

  const {
    showCallClerkConfirm,
    setShowCallClerkConfirm,
    handleCallClerk,
    handleConfirmCallClerk,
  } = useClerkCall({
    activeStoreId: active_store_id || '',
    activeDiningTableId: active_dining_table_id || '',
  });

  // 会計リクエスト状態（ADR 0068）。初期表示で取得し、REQUESTED / ACKNOWLEDGED 中はポーリングで追従する。
  const { state: checkoutState, setState: setCheckoutState } = useCheckoutRequestState({
    activeStoreId: active_store_id || '',
    activeDiningTableId: active_dining_table_id || '',
  });

  const {
    open: showCheckoutRequest,
    setOpen: setShowCheckoutRequest,
    methods: paymentMethods,
    loadingMethods,
    submitting: checkoutRequestSubmitting,
    sent: checkoutRequestSent,
    setSent: setCheckoutRequestSent,
    error: checkoutRequestError,
    handleOpenCheckoutRequest,
    handleSelectPaymentMethod,
  } = useCheckoutRequest({
    activeStoreId: active_store_id || '',
    activeDiningTableId: active_dining_table_id || '',
    // 送信成功時は PUT 応答で即座に REQUESTED 表示へ切り替える
    onStateChange: setCheckoutState,
  });

  // 店員が会計リクエストを受領したら顧客の新規注文を締め切る。店員代行注文（by-clerk）は止めない（会計直前の伝票調整は店員の正当な操作）。
  const orderingClosed = userType === 'customer' && checkoutState?.status === 'ACKNOWLEDGED';

  // メニューの追加ボタン: 受付済みなら数量モーダルを開かず案内だけ出す
  const handleMenuAddClick = (item: OrderItem) => {
    if (orderingClosed) {
      setOrderingClosedNotice(true);
      return;
    }
    handleAddClick(item);
  };

  useEffect(() => {
    if (active_store_id && active_dining_table_id) {
      // メニュー情報を取得
      dispatch(fetchOrderMenus({ active_store_id, active_dining_table_id }))
        .unwrap()
        .then(() => {
          // メニュー情報取得後にカート情報を取得
          dispatch(fetchCart({ 
            activeStoreId: active_store_id, 
            activeDiningTableId: active_dining_table_id, 
            userType 
          }));
        })
        .catch((error) => {
          if (error) {
            navigate(`/public/error?message=${encodeURIComponent('ご利用が開始前か、ご利用が終了しています。')}`);
          }
        });
    }
  }, [active_store_id, active_dining_table_id, dispatch, navigate, userType]);

  useEffect(() => {
    if (categories.length > 0) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories]);

  // カート追加が拒否された（例: 売り切れ）場合はメニューを再取得して最新の売り切れ表示に同期する
  useEffect(() => {
    if (cartError && active_store_id && active_dining_table_id) {
      dispatch(fetchOrderMenus({ active_store_id, active_dining_table_id }));
    }
  }, [cartError, active_store_id, active_dining_table_id, dispatch]);

  const handleShowCart = () => {
    setShowCart(true);
  };

  const selectedCategoryItems = categories
    .find((category) => category.id === selectedCategory)
    ?.items || [];

  // カート内のアイテム数の合計を計算
  const cartItemCount = carts ? carts.reduce((total, item) => total + item.quantity, 0) : 0;

  const handleConfirmAddOrUpdate = (menuOptionId: string | null) => {
    if (selectedItem) {
      const cartItem: CartItem = {
        id: `${selectedItem.id}${menuOptionId ? `_${menuOptionId}` : '_parent'}`,
        menuId: selectedItem.id,
        menuOptionId: menuOptionId,
        name: selectedItem.name,
        price: selectedItem.price,
        quantity: quantity,
        imageUrl: selectedItem.imageUrl || '',
      };

      handleAddToCart(selectedItem, quantity, menuOptionId);
    }
    setShowQuantityModal(false);
    setQuantity(1);
  };

  const cartItemsWithDetails = useMemo(() => {
    // メニューごとにアイテムをグループ化
    const groupedItems = carts.reduce((acc, item) => {
      // メニュー情報を取得
      const menuItem = categories
        .flatMap(category => category.items)
        .find(menu => menu.id === item.menuId);

      if (!menuItem) return acc;

      // メニューオプション情報を取得
      let optionName = '';
      let optionPrice = 0;
      if (item.menuOptionId && menuOptions?.menuOptionGroups) {
        const option = menuOptions.menuOptionGroups
          .filter(group => group.menuId === item.menuId)
          .flatMap(group => group.menuOptionGroup.menuOptions)
          .find(opt => opt.menuOptionId === item.menuOptionId);

        if (option) {
          optionName = option.menuOptionName;
          optionPrice = option.price || 0;
          if (option.priceType === 'DISCOUNT') {
            optionPrice = -optionPrice;
          }
        }
      }

      const cartItem = {
        id: item.id,
        menuId: item.menuId,
        menuOptionId: item.menuOptionId,
        name: menuItem.name,
        optionName,
        imageUrl: menuItem.imageUrl,
        basePrice: menuItem.price,
        optionPrice,
        quantity: item.quantity,
      };

      // メニューIDをキーとしてグループ化
      const key = menuItem.id;
      if (!acc[key]) {
        acc[key] = {
          menuId: menuItem.id,
          name: menuItem.name,
          imageUrl: menuItem.imageUrl,
          basePrice: menuItem.price,
          items: []
        };
      }
      acc[key].items.push(cartItem);

      return acc;
    }, {} as Record<string, {
      menuId: string;
      name: string;
      imageUrl: string;
      basePrice: number;
      items: Array<{
        id: string;
        menuId: string;
        menuOptionId: string | null;
        name: string;
        optionName: string;
        imageUrl: string;
        basePrice: number;
        optionPrice: number;
        quantity: number;
      }>;
    }>);

    // グループ化されたアイテムを配列に変換
    return Object.values(groupedItems).map(group => ({
      id: group.menuId,
      menuId: group.menuId,
      menuOptionId: null,
      name: group.name,
      imageUrl: group.imageUrl,
      price: group.basePrice,
      quantity: group.items.reduce((sum, item) => sum + item.quantity, 0),
      options: group.items.map(item => ({
        id: item.id,
        menuOptionId: item.menuOptionId,
        name: item.optionName,
        price: item.basePrice + item.optionPrice,
        quantity: item.quantity
      }))
    }));
  }, [carts, categories, menuOptions]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PublicHeader
        showLogo={false}
        storeName={storeName || undefined}
        buttons={[
          // 重要操作（会計・カート）はブランド塗り(contained)、通常操作（店員呼び出し）は白地ブランド文字(outlined)。
          // 会計受付済み（ACKNOWLEDGED）の顧客は再リクエスト不可。REQUESTED 中は支払方法の変更・再通知のため押せる。
          { text: 'お会計はこちら', onClick: handleOpenCheckoutRequest, icon: <ReceiptLongIcon />, disabled: orderingClosed },
          { text: '店員を呼ぶ', onClick: handleCallClerk, icon: <NotificationsIcon />, variant: 'outlined' },
          {
            text: 'カート一覧',
            onClick: handleShowCart,
            icon: (
              // ブランドオレンジのボタン上でも数が読めるよう白地バッジにし、追加のたびにポップさせる。
              <Badge
                key={cartItemCount}
                badgeContent={cartItemCount}
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: 'common.white',
                    color: 'primary.main',
                    fontWeight: 700,
                    border: '1.5px solid',
                    borderColor: 'primary.main', // 白背景へはみ出す部分でも輪郭が出るようにする
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    animation: cartItemCount > 0 ? 'cart-badge-pop 0.3s ease' : 'none',
                  },
                  '@keyframes cart-badge-pop': {
                    '0%': { transform: 'scale(1) translate(50%, -50%)' },
                    '50%': { transform: 'scale(1.5) translate(35%, -35%)' },
                    '100%': { transform: 'scale(1) translate(50%, -50%)' },
                  },
                }}
              >
                <ShoppingCartIcon />
              </Badge>
            ),
          },
        ]}
      />
      {/* 会計リクエストの状態バナー（ADR 0068）。NONE なら何も出ない。 */}
      <CheckoutStatusBanner state={checkoutState} />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1, width: '100%' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <CategoryTabs
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />
            <MenuList
              items={selectedCategoryItems}
              onAddClick={handleMenuAddClick}
              cloudFrontUrl={CLOUDFRONT_URL || ''}
              template={orderMenuTemplate}
            />
          </>
        )}
      </Container>

      <QuantitySelectionModal
        open={showQuantityModal}
        onClose={() => {
          setShowQuantityModal(false);
          setQuantity(1);
        }}
        onConfirm={(menuOptionId) => {
          if (selectedItem) {
            handleAddToCart(selectedItem, quantity, menuOptionId);
            setShowQuantityModal(false);
            setQuantity(1);
          }
        }}
        selectedItem={selectedItem}
        quantity={quantity}
        onQuantityChange={setQuantity}
        cloudFrontUrl={CLOUDFRONT_URL || ''}
      />

      <Snackbar
        open={orderingClosedNotice}
        autoHideDuration={4000}
        onClose={() => setOrderingClosedNotice(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setOrderingClosedNotice(false)}>
          お会計受付済みのためご注文いただけません
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!cartError}
        autoHideDuration={5000}
        onClose={clearCartError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={clearCartError}>
          {cartError}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={showCallClerkConfirm}
        onClose={() => setShowCallClerkConfirm(false)}
        onConfirm={handleConfirmCallClerk}
        title="店員呼び出しの確認"
        message="店員を呼びますか？"
        confirmText="呼び出す"
      />

      <CheckoutRequestDialog
        open={showCheckoutRequest}
        methods={paymentMethods}
        loadingMethods={loadingMethods}
        submitting={checkoutRequestSubmitting}
        error={checkoutRequestError}
        onSelect={handleSelectPaymentMethod}
        onClose={() => setShowCheckoutRequest(false)}
        isReRequest={checkoutState?.status === 'REQUESTED'}
      />

      <Snackbar
        open={checkoutRequestSent}
        autoHideDuration={5000}
        onClose={() => setCheckoutRequestSent(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCheckoutRequestSent(false)}>
          お会計の希望を店員にお知らせしました。
        </Alert>
      </Snackbar>

      {showCart && (
        <CartList
          cart={cartItemsWithDetails}
          onClose={() => setShowCart(false)}
          onQuantityChange={handleCartItemQuantity}
          onRemoveItem={handleRemoveItem}
          handleConfirmDelete={handleConfirmDelete}
          activeStoreId={active_store_id || ''}
          activeDiningTableId={active_dining_table_id || ''}
          onCheckoutSuccess={handleCheckoutSuccess}
          clerkId={clerk_id}
          orderingClosed={orderingClosed}
        />
      )}

      <PublicFooter refSource="order" />
    </Box>
  );
};

export default Order;